const EntityService = require("../services/entityService").default;

class UtilCapaPropostaPDF {
  static logoCache = null;

  static async carregarLogo() {
    if (this.logoCache) return this.logoCache;

    try {
      const response = await fetch("/img/logo-delta-ducon.png");
      if (!response.ok) return null;

      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.logoCache = reader.result;
          resolve(this.logoCache);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  }

  static texto(valor) {
    if (valor === null || valor === undefined) return "";
    return String(valor).trim();
  }

  static primeiroTexto(...valores) {
    for (const valor of valores) {
      const texto = this.texto(valor);
      if (texto) return texto;
    }
    return "";
  }

  // WORKAROUND (ver codificarExtrasSalesOrderEmObservacao / extrairExtrasSalesOrderDeObservacao
  // em solicitacaoDeOrcamentoRules.js): enquanto tipoPropostaOrcamento e as datas do bloco Comercial
  // não existem como propriedades reais no Schema SalesOrder, elas viajam escondidas dentro do texto
  // de "observacao" (bloco JSON marcado com [[SO_EXTRA_DATA:...]]). Essa função existe para blindar a
  // Capa da Proposta contra esse marcador — independentemente de o "observacao" recebido já ter sido
  // limpo ou não pela tela que chamou este gerador — para que ele nunca apareça ao usuário final.
  static limparMarcadorExtras(valor) {
    const texto = this.texto(valor);
    if (!texto) return "";
    return texto.replace(/\n?\[\[SO_EXTRA_DATA:[\s\S]*?\]\]\s*$/, "").replace(/\s+$/, "");
  }

  static formatarData(valor) {
    if (!valor) return "";
    const texto = this.texto(valor);
    const matchISO = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matchISO) {
      const [, ano, mes, dia] = matchISO;
      return `${dia}/${mes}/${ano}`;
    }
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return texto;
    return data.toLocaleDateString("pt-BR");
  }

  static formatarCnpj(valor) {
    const somenteNumeros = this.texto(valor).replace(/\D+/g, "");
    if (somenteNumeros.length !== 14) return this.texto(valor);
    return somenteNumeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  }

  static formatarCep(valor) {
    const somenteNumeros = this.texto(valor).replace(/\D+/g, "");
    if (somenteNumeros.length !== 8) return this.texto(valor);
    return somenteNumeros.replace(/^(\d{5})(\d{3})$/, "$1-$2");
  }

  static parseNumero(valor) {
    if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
    if (valor === null || valor === undefined || valor === "") return 0;
    const texto = String(valor).trim();
    const textoNormalizado = texto
      .replace(/\s/g, "")
      .replace(/(\d)\.(?=\d{3}(\D|$))/g, "$1")
      .replace(",", ".");
    const numero = Number(textoNormalizado);
    return Number.isFinite(numero) ? numero : 0;
  }

  static formatarNumero(valor, casas = 2) {
    return this.parseNumero(valor).toLocaleString("pt-BR", {
      minimumFractionDigits: casas,
      maximumFractionDigits: casas
    });
  }

  static extrairId(valor) {
    if (!valor) return "";
    if (Array.isArray(valor)) return this.extrairId(valor[0]);
    if (typeof valor === "object") {
      return valor.id || valor._id || valor.value || "";
    }
    return valor;
  }

  static async buscarRegistro(serviceName, id) {
    if (!id) return null;

    try {
      const service = new EntityService(serviceName);
      const response = await service.getById(id);
      return response?.data?.data?._currentData || response?.data?.data || null;
    } catch (error) {
      return null;
    }
  }

  static async enriquecerDados(dado = {}) {
    const dados = { ...dado };

    const solicitacaoId = this.extrairId(dados.id || dados._id);
    if (solicitacaoId) {
      const solicitacaoCompleta = await this.buscarRegistro("SalesOrder", solicitacaoId);
      if (solicitacaoCompleta && typeof solicitacaoCompleta === "object") {
        Object.assign(dados, solicitacaoCompleta);
      }
    }

    const clienteId = this.extrairId(dados.cliente);
    if (clienteId) {
      const clienteCompleto = await this.buscarRegistro("Cliente", clienteId);
      if (clienteCompleto && typeof clienteCompleto === "object") {
        dados.clienteDetalhes = clienteCompleto;
      }
    }

    const contatoComercialId = this.extrairId(dados.contatoComercial || dados.contato);
    if (contatoComercialId) {
      const contatoComercial = await this.buscarRegistro("ClienteContatos", contatoComercialId);
      if (contatoComercial && typeof contatoComercial === "object") {
        dados.contatoComercialDetalhes = contatoComercial;
      }
    }

    const contatoTecnicoId = this.extrairId(dados.contatoTecnico);
    if (contatoTecnicoId) {
      const contatoTecnico = await this.buscarRegistro("ClienteContatos", contatoTecnicoId);
      if (contatoTecnico && typeof contatoTecnico === "object") {
        dados.contatoTecnicoDetalhes = contatoTecnico;
      }
    }

    const vendedorId = this.extrairId(dados.vendedor);
    if (vendedorId) {
      const vendedor = await this.buscarRegistro("Collaborator", vendedorId);
      if (vendedor && typeof vendedor === "object") {
        dados.vendedorDetalhes = vendedor;
      }
    }

    const orcamentistaId = this.extrairId(dados.orcamentista);
    if (orcamentistaId) {
      const orcamentista = await this.buscarRegistro("Collaborator", orcamentistaId);
      if (orcamentista && typeof orcamentista === "object") {
        dados.orcamentistaDetalhes = orcamentista;
      }
    }

    return dados;
  }

  static selecionarTextoCampo(objeto, candidatos = []) {
    if (!objeto) return "";
    for (const candidato of candidatos) {
      const valor = this.texto(objeto?.[candidato]);
      if (valor) return valor;
    }
    return "";
  }

  static obterClienteFonte(dados = {}) {
    const clienteDetalhes = dados?.clienteDetalhes && typeof dados.clienteDetalhes === "object"
      ? dados.clienteDetalhes
      : {};
    const clienteOriginal = dados?.cliente && typeof dados.cliente === "object"
      ? dados.cliente
      : {};

    return {
      ...clienteOriginal,
      ...clienteDetalhes,
    };
  }

  static construirEnderecoCliente(dados = {}) {
    const cliente = this.obterClienteFonte(dados);
    const faturamentoBairro = this.texto(cliente.faturamentoBairro) === "---" ? "" : cliente.faturamentoBairro;
    const faturamentoComplemento = this.texto(cliente.faturamentoComplemento) === "---" ? "" : cliente.faturamentoComplemento;
    const endereco = this.primeiroTexto(
      cliente.faturamentoEndereco,
      cliente.endereco,
      cliente.logradouro,
      cliente.rua,
      cliente.address,
      cliente.addressLine1
    );
    const numero = this.primeiroTexto(cliente.faturamentoNumero, cliente.numero, cliente.addressNumber);
    const bairro = this.primeiroTexto(faturamentoBairro, cliente.bairro, cliente.neighborhood);
    const complemento = this.primeiroTexto(faturamentoComplemento, cliente.complemento, cliente.addressLine2);
    const cidade = this.primeiroTexto(cliente.faturamentoCidadeNome, cliente.cidade, cliente.municipio, cliente.city);
    const uf = this.primeiroTexto(cliente.faturamentoEstadoNome, cliente.uf, cliente.estado, cliente.state);
    const cep = this.formatarCep(this.primeiroTexto(cliente.faturamentoCEP, cliente.cep, cliente.zipCode, cliente.codigoPostal));

    const partes = [];
    if (endereco) {
      const enderecoComNumero = numero ? `${endereco}, ${numero}` : endereco;
      partes.push(enderecoComNumero);
    }
    if (bairro) partes.push(bairro);
    if (complemento) partes.push(complemento);
    if (cidade || uf) partes.push([cidade, uf].filter(Boolean).join(" - "));
    if (cep) partes.push(`CEP ${cep}`);
    return partes.join(" | ");
  }

  static construirLinhaContato(contato = {}) {
    const nome = this.primeiroTexto(contato.nome, contato.name, contato.contatoNome);
    const telefone = this.primeiroTexto(contato.telefone, contato.phone, contato.telefoneFixo);
    const celular = this.primeiroTexto(contato.celular, contato.mobile, contato.celularContato);
    const email = this.primeiroTexto(contato.email, contato.mail);
    return [nome, telefone, celular, email].filter(Boolean).join(" | ");
  }

  static itemLabel(texto) {
    return {
      text: texto,
      style: "campoLabel",
      fillColor: "#eef3f8",
      margin: [6, 5, 6, 4]
    };
  }

  static itemValor(texto) {
    return {
      text: texto || "",
      style: "campoValor",
      margin: [6, 5, 6, 4]
    };
  }

  static blocoCampo(label, valor, largura = "50%") {
    return {
      width: largura,
      table: {
        widths: ["36%", "64%"],
        body: [[this.itemLabel(label), this.itemValor(valor)]]
      },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => "#c8d3df",
        vLineColor: () => "#c8d3df",
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0
      }
    };
  }

  static blocoTexto(label, valor) {
    return {
      table: {
        widths: ["100%"],
        body: [
          [this.itemLabel(label)],
          [
            {
              text: valor || "",
              style: "campoValor",
              minHeight: 72,
              margin: [6, 8, 6, 8]
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => "#c8d3df",
        vLineColor: () => "#c8d3df",
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0
      }
    };
  }

  static normalizarTextoComparacao(valor) {
    return this.texto(valor)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
  }

  static campoLinha(label, valor, labelWidth = 112) {
    return {
      table: {
        widths: [labelWidth, "*"],
        body: [[this.itemLabel(label), this.itemValor(valor)]]
      },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => "#c8d3df",
        vLineColor: () => "#c8d3df",
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0
      }
    };
  }

  static checkboxCanvas(marcado = false) {
    const itens = [
      { type: "rect", x: 0, y: 0, w: 10, h: 10, lineWidth: 0.8, lineColor: "#444" }
    ];

    if (marcado) {
      itens.push(
        { type: "line", x1: 2, y1: 2, x2: 8, y2: 8, lineWidth: 1, lineColor: "#444" },
        { type: "line", x1: 8, y1: 2, x2: 2, y2: 8, lineWidth: 1, lineColor: "#444" }
      );
    }

    return { canvas: itens, width: 12, margin: [0, 1, 4, 0] };
  }

  static checkboxOption(label, marcado = false) {
    return {
      columns: [
        this.checkboxCanvas(marcado),
        { text: label, fontSize: 9, margin: [0, 0, 0, 0] }
      ],
      columnGap: 4,
      width: "auto"
    };
  }

  static interpretarSimNao(valor) {
    if (valor === true) return "SIM";
    if (valor === false) return "NAO";
    const texto = this.normalizarTextoComparacao(valor);
    if (!texto) return "";
    if (texto.includes("SIM") || texto.includes("TRUE") || texto.includes("1")) return "SIM";
    if (texto.includes("NAO") || texto.includes("NÃO") || texto.includes("FALSE") || texto.includes("0")) return "NAO";
    return "";
  }

  static grupoSimNao(valor) {
    const marcado = this.interpretarSimNao(valor);
    return {
      columns: [
        this.checkboxOption("Sim", marcado === "SIM"),
        this.checkboxOption("Não", marcado === "NAO")
      ],
      columnGap: 12
    };
  }

  static grupoOpcoes(valor, opcoes = []) {
    const texto = this.normalizarTextoComparacao(valor);
    return {
      columns: opcoes.map((opcao) => this.checkboxOption(opcao, texto.includes(this.normalizarTextoComparacao(opcao)))),
      columnGap: 12
    };
  }

  static secaoTitulo(texto) {
    return {
      table: {
        widths: ["*"],
        body: [[{ text: texto, style: "secao" }]]
      },
      layout: "noBorders",
      margin: [0, 8, 0, 4]
    };
  }

  static normalizarTabelasPdfmake(node) {
    if (Array.isArray(node)) {
      for (let index = 0; index < node.length; index += 1) {
        if (node[index] === undefined || node[index] === null) node[index] = { text: "" };
        this.normalizarTabelasPdfmake(node[index]);
      }
      return node;
    }

    if (!node || typeof node !== "object") return node;

    if (node.table && typeof node.table === "object") {
      const tabela = node.table;
      const body = Array.isArray(tabela.body) ? tabela.body : [];
      const largurasOriginais = Array.isArray(tabela.widths) ? tabela.widths : [];
      const contarColunas = (linha) => (Array.isArray(linha) ? linha.reduce((total, celula) => {
        const span = Number(celula?.colSpan || 1);
        return total + (Number.isFinite(span) && span > 0 ? span : 1);
      }, 0) : 1);
      const quantidadeColunas = largurasOriginais.length || Math.max(0, ...body.map(contarColunas));

      if (quantidadeColunas > 0) {
        tabela.widths = [
          ...largurasOriginais,
          ...Array(Math.max(0, quantidadeColunas - largurasOriginais.length)).fill("*")
        ];
        tabela.body = Array.from({ length: body.length }, (_, indiceLinha) => {
          const linha = body[indiceLinha];
          const linhaNormalizada = Array.isArray(linha) ? Array.from(linha) : (linha === undefined || linha === null ? [] : [linha]);
          const celulasNormalizadas = [];
          let colunasUsadas = 0;
          linhaNormalizada.forEach((celula) => {
            if (colunasUsadas >= quantidadeColunas) return;
            const celulaNormalizada = celula === undefined || celula === null ? { text: "" } : celula;
            const span = Number(celulaNormalizada?.colSpan || 1);
            const colSpan = Number.isFinite(span) && span > 0
              ? Math.min(Math.floor(span), quantidadeColunas - colunasUsadas)
              : 1;
            celulasNormalizadas.push(colSpan > 1 ? { ...celulaNormalizada, colSpan } : celulaNormalizada);
            colunasUsadas += colSpan;
          });
          while (colunasUsadas < quantidadeColunas) {
            celulasNormalizadas.push({ text: "" });
            colunasUsadas += 1;
          }
          return celulasNormalizadas;
        });
      }

      tabela.body.forEach((linha) => {
        if (Array.isArray(linha)) {
          linha.forEach((celula) => this.normalizarTabelasPdfmake(celula));
        }
      });
    }

    Object.keys(node).forEach((chave) => {
      if (chave !== "table") this.normalizarTabelasPdfmake(node[chave]);
    });

    return node;
  }

  static validarTabelasPdfmake(node, caminho = "root") {
    if (Array.isArray(node)) {
      node.forEach((item, indice) => this.validarTabelasPdfmake(item, `${caminho}[${indice}]`));
      return;
    }

    if (!node || typeof node !== "object") return;

    if (node.table && typeof node.table === "object") {
      const widths = Array.isArray(node.table.widths) ? node.table.widths : [];
      const body = Array.isArray(node.table.body) ? node.table.body : [];
      body.forEach((linha, indice) => {
        const colunas = Array.isArray(linha) ? linha.reduce((total, celula) => total + Number(celula?.colSpan || 1), 0) : 0;
        if (colunas !== widths.length) {
          throw new Error(`Tabela PDF inválida em ${caminho}. Linha ${indice}: ${colunas} colunas para ${widths.length} widths.`);
        }
      });
    }

    Object.keys(node).forEach((chave) => {
      this.validarTabelasPdfmake(node[chave], `${caminho}.${chave}`);
    });
  }

  static criarDefinicao(dados = {}, logo = null) {
    const codigo = this.primeiroTexto(dados.numeroProposta, dados.numeroSolicitacao, dados.code, dados.codigo, dados.version);
    const dataEmissao = this.formatarData(dados.dateGenerate || dados.dataGeracao || dados.dataSolicitacao || new Date());
    const cliente = this.obterClienteFonte(dados);
    const vendedor = dados.vendedorDetalhes || {};
    const orcamentista = dados.orcamentistaDetalhes || {};
    const contatoComercial = dados.contatoComercialDetalhes || {};
    const contatoTecnico = dados.contatoTecnicoDetalhes || {};

    const razaoSocial = this.primeiroTexto(
      dados.clienteNome,
      cliente.razaoSocial,
      cliente.nomeFantasia,
      cliente.nome
    );
    const nomeFantasia = this.primeiroTexto(cliente.nomeFantasia, cliente.nome, dados.clienteNome);
    const cnpj = this.formatarCnpj(this.primeiroTexto(dados.clienteCnpj, cliente.cnpj, cliente.cpfCnpj));
    const inscricaoEstadual = this.primeiroTexto(cliente.inscricaoEstadual, cliente.ie, cliente.inscricaoMunicipal);
    const enderecoCliente = this.construirEnderecoCliente(dados);
    const contatoLinha = this.construirLinhaContato(contatoComercial);
    const contatoTecnicoLinha = this.construirLinhaContato(contatoTecnico);

    const responsavelComercial = this.primeiroTexto(dados.contatoComercialNome, contatoComercial.nome, dados.contatoNome);
    const vendedorNome = this.primeiroTexto(dados.vendedorNome, vendedor.nome, vendedor.name);
    const orcamentistaNome = this.primeiroTexto(dados.orcamentistaNome, orcamentista.nome, orcamentista.name);
    const tipoEquipamento = this.primeiroTexto(dados.tipoDeEquipamentoDescricao, dados.tipoDeEquipamentoSigla, dados.tipoDescricao);
    const origem = this.primeiroTexto(dados.origem, dados.origemSchema);
    const referenciaCliente = this.primeiroTexto(dados.cliente_referencia, dados.referenciaCliente);
    const dataSolicitacao = this.formatarData(dados.dataSolicitacao);
    const dataDesejada = this.formatarData(dados.dataDesejada);
    const dataConclusao = this.formatarData(dados.dataConclusao);
    // Data Desejada Comercial (bloco Comercial) — exibida ao lado da Data Desejada Técnica no
    // cabeçalho, junto das revisões Comercial/Técnica (Fase 4).
    const dataDesejadaComercial = this.formatarData(dados.dataDesejadaComercial);
    const nivelProposta = this.primeiroTexto(dados.nivel);
    const revisaoComercial = this.primeiroTexto(dados.version, dados.revisao, dados.ultimaRevisao, "ORIGINAL");
    const revisaoTecnica = this.primeiroTexto(dados.versionTec, "ORIGINAL");
    const observacaoLimpa = this.limparMarcadorExtras(dados.observacao);
    const descricao = this.primeiroTexto(dados.projetoDescricao, dados.descricao, observacaoLimpa);
    const observacoes = this.primeiroTexto(observacaoLimpa, dados.observacoes);
    const clienteCidade = this.primeiroTexto(cliente.faturamentoCidadeNome, cliente.cidade, cliente.municipio, cliente.city);
    const clienteEstado = this.primeiroTexto(cliente.faturamentoEstadoNome, cliente.uf, cliente.estado, cliente.state);
    const clienteCep = this.formatarCep(this.primeiroTexto(cliente.faturamentoCEP, cliente.cep, cliente.zipCode, cliente.codigoPostal));
    const clienteEndereco = this.construirEnderecoCliente(dados);
    const contatoCargo = this.primeiroTexto(contatoComercial.cargo, contatoComercial.funcao, contatoComercial.position, contatoComercial.title);
    const contatoTelefone = this.primeiroTexto(contatoComercial.telefone, contatoComercial.phone, contatoComercial.fone, contatoComercial.celular);
    const contatoEmail = this.primeiroTexto(contatoComercial.email, contatoComercial.mail);
    // FASE 4 (a pedido do usuário): "Proposta Técnica Comercial Separada" agora é derivado do Tipo
    // de Orçamento — se a Solicitação estiver marcada como "Comercial e Técnica" (proposta única
    // cobrindo as duas partes), a resposta é "Não"; qualquer outro Tipo de Orçamento responde "Sim".
    const ehComercialETecnica = this.normalizarTextoComparacao(dados.tipoPropostaOrcamento) === "COMERCIALETECNICA";
    const propostaSeparada = !ehComercialETecnica;
    const consulta = this.primeiroTexto(dados.consulta, dados.origem, dataSolicitacao);
    const vistoVendedor = this.primeiroTexto(dados.vistoVendedor, vendedorNome);
    const itensSolicitacao = Array.isArray(dados.itens) ? dados.itens : [];
    const linhasItensSolicitacao = itensSolicitacao
      .map((item = {}, index) => {
        const quantidade = this.parseNumero(item.quantidade);
        const precoSemIpi = this.parseNumero(item.precoVenda_sIPI_cICMS);
        const valorIpi = this.parseNumero(item.valorIPI);
        const valorTotal = this.parseNumero(item.valorTotal) || (quantidade * precoSemIpi);

        return [
          this.itemValor(String(index + 1)),
          this.itemValor(this.primeiroTexto(item.descricao, item.itemDescricao, item.itemCodigo, "-")),
          this.itemValor(this.formatarNumero(quantidade, 2)),
          this.itemValor(this.formatarNumero(precoSemIpi)),
          this.itemValor(this.formatarNumero(valorIpi)),
          this.itemValor(this.formatarNumero(valorTotal))
        ];
      });

    const valoresComerciaisSchema = {
      valorDisponivel: this.parseNumero(dados.valorDisponivel),
      precoVendaSemIpi: this.parseNumero(dados.precoVenda_sIPI_cICMS || dados._mirror_valor_sIPI_cICMS),
      valorICMS: this.parseNumero(dados.valorICMS),
      valorIPI: this.parseNumero(dados.valorIPI),
      precoVendaComIpi: this.parseNumero(dados.precoVenda_cIPI_cICMS)
    };

    const possuiValoresSemItens =
      linhasItensSolicitacao.length === 0 &&
      Object.values(valoresComerciaisSchema).some((valor) => Number(valor) > 0);

    return {
      pageSize: "A4",
      pageMargins: [18, 16, 18, 18],
      defaultStyle: {
        fontSize: 9,
        color: "#1b1f24"
      },
      styles: {
        titulo: {
          fontSize: 18,
          bold: true,
          color: "#0f3b68",
          alignment: "right"
        },
        subtitulo: {
          fontSize: 10,
          color: "#5e6c7b",
          alignment: "right"
        },
        secao: {
          fontSize: 10,
          bold: true,
          color: "#ffffff",
          fillColor: "#0f3b68",
          margin: [6, 4, 6, 4]
        },
        campoLabel: {
          bold: true,
          fontSize: 8,
          color: "#23313f"
        },
        campoValor: {
          fontSize: 9,
          color: "#111827"
        },
        rodape: {
          fontSize: 8,
          color: "#5e6c7b"
        }
      },
      content: [
        {
          columns: [
            logo
              ? { image: logo, width: 110 }
              : { text: "" },
            {
              stack: [
                { text: "CAPA DE PROPOSTA", style: "titulo" },
                { text: `Proposta ${codigo || ""}`.trim(), style: "subtitulo", margin: [0, 2, 0, 0] },
                { text: `Emissão: ${dataEmissao || ""}`, style: "subtitulo", margin: [0, 2, 0, 0] }
              ],
              width: "*"
            }
          ],
          margin: [0, 0, 0, 8]
        },
        {
          canvas: [{ type: "line", x1: 0, y1: 0, x2: 560, y2: 0, lineWidth: 1, lineColor: "#0f3b68" }],
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            widths: ["28%", "22%", "20%", "30%"],
            body: [[
              this.itemLabel("Nº PROPOSTA"),
              this.itemValor(codigo),
              this.itemLabel("DATA"),
              this.itemValor(dataEmissao)
            ], [
              this.itemLabel("Revisão Comercial"),
              this.itemValor(revisaoComercial),
              this.itemLabel("Revisão Técnica"),
              this.itemValor(revisaoTecnica)
            ], [
              this.itemLabel("Data Desejada Comercial"),
              this.itemValor(dataDesejadaComercial),
              this.itemLabel("Data Desejada Técnica"),
              this.itemValor(dataDesejada)
            ]]
          },
          layout: {
            hLineWidth: () => 0.6,
            vLineWidth: () => 0.6,
            hLineColor: () => "#c8d3df",
            vLineColor: () => "#c8d3df",
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        },
        {
          text: "",
          margin: [0, 6, 0, 0]
        },
        {
          table: {
            widths: ["100%"],
            body: [[{ text: "DADOS DO CLIENTE", style: "secao" }]]
          },
          layout: "noBorders",
          margin: [0, 0, 0, 3]
        },
        {
          table: {
            widths: ["50%", "50%"],
            body: [[
              this.blocoCampo("Razão Social", razaoSocial),
              this.blocoCampo("Nome Fantasia", nomeFantasia)
            ], [
              this.blocoCampo("CNPJ", cnpj),
              this.blocoCampo("Inscrição Estadual", inscricaoEstadual)
            ], [
              this.blocoCampo("Endereço", enderecoCliente),
              this.blocoCampo("Referência do Cliente", referenciaCliente)
            ], [
              this.blocoCampo("Contato Comercial", responsavelComercial),
              this.blocoCampo("Contato Comercial Dados", contatoLinha)
            ], [
              this.blocoCampo("Contato Técnico", this.primeiroTexto(dados.contatoTecnicoNome, contatoTecnico.nome, contatoTecnico.name)),
              this.blocoCampo("Contato Técnico Dados", contatoTecnicoLinha)
            ]]
          },
          layout: "noBorders",
          margin: [0, 0, 0, 8]
        },
        {
          table: {
            widths: ["100%"],
            body: [[{ text: "DADOS DA PROPOSTA", style: "secao" }]]
          },
          layout: "noBorders",
          margin: [0, 0, 0, 3]
        },
        {
          table: {
            widths: ["50%", "50%"],
            body: [[
              this.blocoCampo("Vendedor", vendedorNome),
              this.blocoCampo("Orçamentista", orcamentistaNome)
            ], [
              this.blocoCampo("Tipo de Equipamento", tipoEquipamento),
              this.blocoCampo("Origem", origem)
            ], [
              this.blocoCampo("Data Solicitação", dataSolicitacao),
              this.blocoCampo("Data Desejada", dataDesejada)
            ], [
              this.blocoCampo("Data Conclusão", dataConclusao),
              this.blocoCampo("Status", this.primeiroTexto(dados.statusDescription, dados.status))
            ], [
              this.blocoCampo("Nível", nivelProposta),
              this.blocoCampo("", "")
            ]]
          },
          layout: "noBorders",
          margin: [0, 0, 0, 8]
        },
        this.blocoTexto("DESCRIÇÃO DO PROJETO", descricao),
        ...(observacoes ? [this.blocoTexto("OBSERVAÇÕES", observacoes), { text: "", margin: [0, 6, 0, 0] }] : []),
        ...(linhasItensSolicitacao.length
          ? [
              {
                text: "",
                margin: [0, 8, 0, 0]
              },
              this.secaoTitulo("ITENS DA SOLICITAÇÃO"),
              {
                table: {
                  widths: ["6%", "46%", "12%", "12%", "12%", "12%"],
                  body: [
                    [
                      this.itemLabel("#"),
                      this.itemLabel("Descrição"),
                      this.itemLabel("Qtd"),
                      this.itemLabel("Vlr. s/IPI"),
                      this.itemLabel("Vlr. IPI"),
                      this.itemLabel("Vlr. Total")
                    ],
                    ...linhasItensSolicitacao
                  ]
                },
                layout: {
                  hLineWidth: () => 0.6,
                  vLineWidth: () => 0.6,
                  hLineColor: () => "#c8d3df",
                  vLineColor: () => "#c8d3df",
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0
                }
              }
            ]
          : (possuiValoresSemItens
            ? [
                {
                  text: "",
                  margin: [0, 8, 0, 0]
                },
                this.secaoTitulo("VALORES DA PROPOSTA"),
                {
                  columns: [
                    this.blocoCampo("Custo Disponível", this.formatarNumero(valoresComerciaisSchema.valorDisponivel), "50%"),
                    this.blocoCampo("Preço Venda s/IPI", this.formatarNumero(valoresComerciaisSchema.precoVendaSemIpi), "50%")
                  ],
                  columnGap: 8,
                  margin: [0, 0, 0, 6]
                },
                {
                  columns: [
                    this.blocoCampo("Valor ICMS", this.formatarNumero(valoresComerciaisSchema.valorICMS), "50%"),
                    this.blocoCampo("Valor IPI", this.formatarNumero(valoresComerciaisSchema.valorIPI), "50%")
                  ],
                  columnGap: 8,
                  margin: [0, 0, 0, 6]
                },
                {
                  columns: [
                    this.blocoCampo("Preço Venda c/IPI c/ICMS", this.formatarNumero(valoresComerciaisSchema.precoVendaComIpi), "50%")
                  ],
                  columnGap: 8,
                  margin: [0, 0, 0, 2]
                }
              ]
            : [])),
        {
          text: "",
          margin: [0, 8, 0, 0]
        },
        {
          table: {
            widths: ["50%", "50%"],
            body: [[
              {
                stack: [
                  { text: "RESPONSÁVEL COMERCIAL", style: "campoLabel", margin: [0, 0, 0, 12] },
                  { canvas: [{ type: "line", x1: 0, y1: 0, x2: 230, y2: 0, lineWidth: 0.8, lineColor: "#6b7280" }] },
                  { text: responsavelComercial || "", alignment: "center", margin: [0, 4, 0, 0] }
                ],
                margin: [4, 8, 4, 4]
              },
              {
                stack: [
                  { text: "RESPONSÁVEL TÉCNICO", style: "campoLabel", margin: [0, 0, 0, 12] },
                  { canvas: [{ type: "line", x1: 0, y1: 0, x2: 230, y2: 0, lineWidth: 0.8, lineColor: "#6b7280" }] },
                  { text: this.primeiroTexto(dados.contatoTecnicoNome, contatoTecnico.nome, contatoTecnico.name), alignment: "center", margin: [0, 4, 0, 0] }
                ],
                margin: [4, 8, 4, 4]
              }
            ]]
          },
          layout: {
            hLineWidth: () => 0.6,
            vLineWidth: () => 0.6,
            hLineColor: () => "#c8d3df",
            vLineColor: () => "#c8d3df",
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          },
          margin: [0, 4, 0, 0]
        },
        this.secaoTitulo("DADOS PARA ORÇAMENTO"),
        {
          table: {
            widths: ["50%", "50%"],
            body: [[
              this.campoLinha("Cliente", razaoSocial || nomeFantasia || this.primeiroTexto(cliente.nome)),
              this.campoLinha("Endereço", clienteEndereco)
            ], [
              this.campoLinha("Cidade", clienteCidade),
              this.campoLinha("Estado", clienteEstado)
            ], [
              this.campoLinha("CEP", clienteCep),
              this.campoLinha("Atenção", responsavelComercial)
            ], [
              this.campoLinha("Cargo", contatoCargo),
              this.campoLinha("Telefone", contatoTelefone)
            ], [
              this.campoLinha("Fax", this.primeiroTexto(dados.fax, contatoComercial.fax)),
              this.campoLinha("e-mail", contatoEmail)
            ], [
              {
                stack: [
                  this.campoLinha("Proposta Técnica Comercial Separada", ""),
                  { margin: [132, -18, 0, 0], ...this.grupoSimNao(propostaSeparada) }
                ]
              },
              { text: "" }
            ], [
              this.campoLinha("Consulta", consulta),
              this.campoLinha("Tipo Eqto", tipoEquipamento)
            ], [
              this.campoLinha("Projeto", descricao),
              this.campoLinha("", "")
            ]]
          },
          layout: "noBorders",
          margin: [0, 0, 0, 8]
        },
        // FASE 4 (a pedido do usuário): as sessões "DADOS COMERCIAIS" e "DADOS TÉCNICOS" (com seus
        // blocos de concorrentes/participação e montagem/supervisão/anexo) foram removidas da Capa
        // da Proposta — permanecem apenas Dados do Cliente, Dados da Proposta, Itens/Valores e
        // Dados para Orçamento.
        {
          columns: [
            { width: "65%", text: "" },
            {
              width: "35%",
              stack: [
                { text: "", margin: [0, 14, 0, 0] },
                { canvas: [{ type: "line", x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 0.8, lineColor: "#333" }] },
                { text: "Visto do Vendedor", alignment: "center", bold: true, margin: [0, 4, 0, 0] },
                { text: vistoVendedor, alignment: "center", margin: [0, 2, 0, 0] }
              ]
            }
          ],
          columnGap: 8,
          margin: [0, 8, 0, 0]
        }
      ],
      footer: (currentPage, pageCount) => ({
        margin: [18, 0, 18, 0],
        columns: [
          { text: `Emissão : ${this.formatarData(dados.dateGenerate || dados.dataGeracao || dados.dataSolicitacao || new Date())} - gerado via ERP DELTADUCON`, style: "rodape", alignment: "left" },
          { text: `Pag.-${currentPage}`, style: "rodape", alignment: "right" }
        ]
      })
    };
  }

  static async gerarCapaProposta(dado = {}) {
    const pdfMake = require("pdfmake/build/pdfmake.js");
    const pdfFonts = await require("pdfmake/build/vfs_fonts.js");
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    const logo = await this.carregarLogo();
    const dadosEnriquecidos = await this.enriquecerDados(dado);
    const definicao = this.normalizarTabelasPdfmake(
      this.criarDefinicao(dadosEnriquecidos, logo)
    );
    this.validarTabelasPdfmake(definicao);
    const nomeArquivo = `Capa_Proposta_${this.texto(dadosEnriquecidos.numeroProposta || dadosEnriquecidos.numeroSolicitacao || dadosEnriquecidos.code || dadosEnriquecidos.codigo || "proposta").replace(/\s+/g, "_")}.pdf`;

    pdfMake.createPdf(definicao).download(nomeArquivo);
  }
}

module.exports = UtilCapaPropostaPDF;
module.exports.default = UtilCapaPropostaPDF;
